import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@src/service';
import { NotificationSettingEntity } from '../entities';
import { IGetNotificationsRequest, IGetNotificationsResponse } from './types';
import { normalizeCount, normalizeList, normalizeSettings } from './normalizers';

export const baseNotificationsApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'baseNotificationsApi',
  tagTypes: ['Notifications', 'NotificationSettings', 'UnreadCount'],
  endpoints: (builder) => ({
    getNotifications: builder.query<IGetNotificationsResponse, IGetNotificationsRequest>({
      query: ({ page, size, unreadOnly }) => ({
        url: '/api/notifications',
        method: 'GET',
        params: { page, size, ...(unreadOnly ? { unreadOnly: true } : {}) },
      }),
      transformResponse: normalizeList,
      providesTags: ['Notifications'],
    }),

    getUnreadCount: builder.query<number, void>({
      query: () => ({
        url: '/api/notifications/unread-count',
        method: 'GET',
      }),
      transformResponse: normalizeCount,
      providesTags: ['UnreadCount'],
    }),

    markNotificationRead: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/notifications/${id}/read`,
        method: 'POST',
      }),
    }),

    markAllNotificationsRead: builder.mutation<number, void>({
      query: () => ({
        url: '/api/notifications/mark-all-read',
        method: 'POST',
      }),
      transformResponse: normalizeCount,
    }),

    getNotificationSettings: builder.query<NotificationSettingEntity[], void>({
      query: () => ({
        url: '/api/notifications/settings',
        method: 'GET',
      }),
      transformResponse: normalizeSettings,
      providesTags: ['NotificationSettings'],
    }),

    updateNotificationSettings: builder.mutation<NotificationSettingEntity[], NotificationSettingEntity[]>({
      query: (settings) => ({
        url: '/api/notifications/settings',
        method: 'PUT',
        data: settings,
      }),
      transformResponse: normalizeSettings,
      invalidatesTags: ['NotificationSettings'],
    }),

    resetNotificationSettings: builder.mutation<NotificationSettingEntity[], void>({
      query: () => ({
        url: '/api/notifications/settings/reset',
        method: 'POST',
      }),
      transformResponse: normalizeSettings,
      invalidatesTags: ['NotificationSettings'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useResetNotificationSettingsMutation,
} = baseNotificationsApi;
